import { useState, useCallback } from "react";
import { ListTodo, Plus } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import {
  useTasks,
  useCreateTask,
  useUpdateTask,
} from "@/hooks/admin/useOperations";
import type { Task, TaskPriority, TaskStatus, CreateTaskInput } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  urgent: "Urgent",
  high: "Haute",
  medium: "Moyenne",
  low: "Basse",
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  urgent: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-stone-100 text-stone-600 border-stone-200",
};

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "À faire",
  in_progress: "En cours",
  done: "Terminée",
  cancelled: "Annulée",
};

const STATUS_COLORS: Record<TaskStatus, string> = {
  todo: "bg-stone-100 text-stone-600 border-stone-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  done: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "todo", label: "À faire" },
      { value: "in_progress", label: "En cours" },
      { value: "done", label: "Terminée" },
      { value: "cancelled", label: "Annulée" },
    ],
  },
  {
    key: "priority",
    label: "Priorité",
    type: "select",
    options: [
      { value: "urgent", label: "Urgent" },
      { value: "high", label: "Haute" },
      { value: "medium", label: "Moyenne" },
      { value: "low", label: "Basse" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create task modal
// ---------------------------------------------------------------------------

interface CreateTaskModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateTaskInput) => Promise<void>;
}

function CreateTaskModal({ open, onClose, isSaving, onSave }: CreateTaskModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setTitle("");
    setDescription("");
    setPriority("medium");
    setAssignedTo("");
    setDueDate("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        assigned_to_id: assignedTo.trim() || undefined,
        due_date: dueDate || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [title, description, priority, assignedTo, dueDate, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle tâche"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Nom de la tâche"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Détails de la tâche…"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">Priorité</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">Date limite</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Assigné à (ID utilisateur)
          </label>
          <input
            type="text"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="UUID de l'utilisateur"
          />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Tasks() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);

  const params = {
    page,
    limit: 20,
    status: (filters.status as TaskStatus) ?? undefined,
    priority: (filters.priority as TaskPriority) ?? undefined,
    date_from: (filters.date_from as string) ?? undefined,
    date_to: (filters.date_to as string) ?? undefined,
  };

  const { data, isLoading } = useTasks(params);
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();

  const tasks = data?.tasks ?? [];
  const pagination = data?.pagination;

  const todoCount = tasks.filter((t) => t.status === "todo").length;
  const inProgressCount = tasks.filter((t) => t.status === "in_progress").length;
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const urgentCount = tasks.filter((t) => t.priority === "urgent").length;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateTaskInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const handleStatusChange = useCallback(
    async (id: string, status: TaskStatus) => {
      await updateMutation.mutateAsync({ id, patch: { status } });
    },
    [updateMutation],
  );

  const columns: readonly ColumnDef<Task>[] = [
    {
      id: "title",
      header: "Titre",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.title}</span>
      ),
    },
    {
      id: "priority",
      header: "Priorité",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${PRIORITY_COLORS[row.priority]}`}
        >
          {PRIORITY_LABELS[row.priority]}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <select
          value={row.status}
          onChange={(e) => handleStatusChange(row.id, e.target.value as TaskStatus)}
          className={`rounded-full border px-2.5 py-0.5 text-xs font-medium focus:outline-none ${STATUS_COLORS[row.status]}`}
        >
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      ),
    },
    {
      id: "assigned_to",
      header: "Assigné à",
      cell: (row) => (
        <span className="text-stone-600">{row.assigned_to_name ?? "—"}</span>
      ),
    },
    {
      id: "due_date",
      header: "Date limite",
      cell: (row) => (
        <span className="text-stone-500">{formatDate(row.due_date)}</span>
      ),
    },
    {
      id: "created_at",
      header: "Créée le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.created_at)}</span>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <ListTodo className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">Tâches</h1>
              <p className="text-sm text-stone-500">Suivi des tâches opérationnelles</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvelle tâche
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="À faire" value={todoCount} />
          <KPICard label="En cours" value={inProgressCount} />
          <KPICard label="Terminées (page)" value={doneCount} />
          <KPICard label="Urgentes" value={urgentCount} />
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar filters={FILTER_CONFIGS} values={filters} onChange={handleFilterChange} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={tasks}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune tâche trouvée."
        />
      </div>

      <CreateTaskModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
